import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DatePipe} from '@angular/common';
import {BreakpointObserver, Breakpoints, BreakpointState} from '@angular/cdk/layout';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, combineLatest, delay, EMPTY, filter, mergeMap, Observable, of, shareReplay, switchMap} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {
  BowkerMetadata,
  CancelHoldDialog,
  CatalogRecord,
  CatalogRecordService,
  ChiliFreshProReview,
  ChiliFreshRating,
  ChiliFreshService,
  ConfirmationDialogComponent,
  FavoriteService,
  Hold,
  HoldService,
  IdentifierType,
  NotificationService,
  PauseHoldDialog,
  UnpauseHoldDialog
} from '@raven';
import {authenticated} from '@store/patron/patron.selectors';
import {Store} from '@ngrx/store';
import {OrganizationSelectors, PatronSelectors} from '@store/store.selectors';
import {PatronService} from '../../../services/patron.service';
import {CreateAccountDialogComponent} from '../../index/create-account.dialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Actions, ofType} from '@ngrx/effects';
import {PatronActions} from '@store/patron/patron.actions';

@Component({
  selector: 'rn-catalog-item-details',
  templateUrl: './catalog-item-details.component.html',
  styleUrl: './catalog-item-details.component.scss',
  standalone: false
})
export class CatalogItemDetailsComponent implements OnInit {
  public readonly patronSelectors = PatronSelectors.Auth;
  public readonly patronStatusSelectors = PatronSelectors.AccountStatus;
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions: Actions = inject(Actions);
  loading = true;

  catalogRecordId: number;
  catalogRecord$: Observable<CatalogRecord>;
  item$: Observable<object>;
  chiliFreshRating$: Observable<ChiliFreshRating>;
  chiliFreshProReviews$: Observable<ChiliFreshProReview[]>;
  hold: Hold;
  relatedTitles$: Observable<Array<CatalogRecord>>;
  refreshHoldsSubject = new BehaviorSubject(true);
  meta$: Observable<BowkerMetadata>;
  CatalogRecord = CatalogRecord;
  mobile = false;

  // mock data for now
  meta: object;
  otherFormats: Array<object>;
  otherFormatCount = 1;

  constructor(
    private catalogRecordService: CatalogRecordService,
    private holdService: HoldService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    public patronService: PatronService,
    private chiliFreshService: ChiliFreshService,
    public favoriteService: FavoriteService,
    private dialog: MatDialog,
    public datePipe: DatePipe,
    private breakpointObserver: BreakpointObserver,
    private store: Store,
  ) {
    this.actions.pipe(
      ofType(PatronActions.placeHoldSuccess),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({hold}) => {
      this.hold = hold;
    });
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: BreakpointState) => {
        this.mobile = state.matches;
      });

    const catalogRecordId$ = this.route.paramMap
      .pipe(
        map(paramMap => Number(paramMap.get('id'))),
        filter(id => Number.isInteger(id)),
        tap(x => {
          this.catalogRecordId = x;
          this.loading = true;
        }),
        shareReplay({bufferSize: 1, refCount: false}),
        takeUntilDestroyed(this.destroyRef));

    this.catalogRecord$ = catalogRecordId$
      .pipe(mergeMap(catalogRecordId => {
        return this.catalogRecordService.getById(catalogRecordId);
      }))
      .pipe(shareReplay({bufferSize: 1, windowTime: undefined, refCount: false}));

    this.chiliFreshRating$ = this.catalogRecord$.pipe(
      switchMap(cr => cr.identifier ? this.chiliFreshService.getCombinedRating([cr.identifier]) : EMPTY),
    );

    this.relatedTitles$ = catalogRecordId$.pipe(
      switchMap(id => this.catalogRecordService.getRelatedTitles(id)),
    );

    this.meta$ = combineLatest([
      this.catalogRecord$,
      this.store.select(OrganizationSelectors.Auth.finishedLoading),
      this.store.select(OrganizationSelectors.Auth.enableBowkerMetadata),
    ]).pipe(
      filter(([, organizationFinishedLoading]) => organizationFinishedLoading),
      switchMap(([cr, , enableBowkerMetadata]) =>
        enableBowkerMetadata && cr.identifier && cr.identifierType === IdentifierType.ISBN
          ? this.catalogRecordService.getMetadata(cr.identifier)
          : of({} as BowkerMetadata)
      )
    );

    const holds$ = authenticated(this.store) ? this.getPatronHolds$() : of(null);

    this.setupMockData();

    combineLatest([this.catalogRecord$, holds$])
      .pipe(
        tap(() => window.scrollTo(0, 0)),
        delay(100),
        tap(() => {
          this.loading = false;
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  setupMockData(): void {
    this.meta = {
      thumbnailImageUrl:
        'https://images-na.ssl-images-amazon.com/images/I/81XWWgotqvL.jpg',
      rating: 2.5,
      ratingCount: 88,
      favorite: false,
      otherFormatCount: 4,
    };
    this.otherFormats = [];
    for (let i = 0; i < 4; i++) {
      this.otherFormats.push({
        title: 'The Paper Palace 2',
        narrator: 'Jerry',
        publisher: 'McNamara',
        duration: '14 hours',
        publicationYear: '2021',
        format: 'Audiobook',
      });
    }
  }

  getPatronHolds$(): Observable<Hold[]> {
    if (!authenticated(this.store)) return of(null);
    return combineLatest([this.catalogRecord$, this.refreshHoldsSubject.asObservable()])
      .pipe(
        switchMap(([catalogRecord,]) => this.holdService.getPatronActiveHoldsByCatalogRecord(catalogRecord.id)),
        tap(holds => {
          if (holds && holds.length > 0) {
            this.hold = holds[0];
          } else {
            this.hold = null;
          }
        })
      );
  }

  addFavorite(): void {
    this.favoriteService.addFavorite(this.catalogRecordId).subscribe();
  }

  deleteFavorite(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {title: `Favorites`, message: 'Are you sure you want to un-favorite this item?'},
      })
      .afterClosed()
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.favoriteService.deleteFavorite(this.catalogRecordId)
          }
        })
      )
      .subscribe();
  }


  placeHold(cr: CatalogRecord): void {
    if (cr?.id > 0) {
      this.store.dispatch(PatronActions.preparePlaceHold(cr));
    }
  }

  onHoldRemoved(): void {
    this.hold = null;
    this.refreshHold();
  }

  refreshHold(): void {
    this.refreshHoldsSubject.next(true);
  }

  pauseHold(): void {
    this.dialog.open(PauseHoldDialog, {
      maxWidth: '95vw',
      data: {holds: [this.hold], refreshHolds: this.refreshHold.bind(this)}
    });
  }

  unpauseHold(): void {
    this.dialog.open(UnpauseHoldDialog, {
      maxWidth: '95vw',
      data: {holds: [this.hold], refreshHolds: this.refreshHold.bind(this)}
    });
  }

  cancelHold(): void {
    this.dialog.open(CancelHoldDialog, {
      maxWidth: '95vw',
      data: {holds: [this.hold], refreshHolds: this.onHoldRemoved.bind(this)}
    });
  }

  async copyLink() {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    this.notificationService.showSnackbarSuccess('Link copied to clipboard');
  }

  isReadyForPickupStatus(hold: Hold): boolean {
    return HoldService.isReadyForPickupStatus(hold);
  }

  isInTransitStatus(hold: Hold): boolean {
    return HoldService.isInTransitStatus(hold);
  }

  isRequestedStatus(hold: Hold): boolean {
    return HoldService.isRequestedStatus(hold);
  }

  isPausedStatus(hold: Hold): boolean {
    return HoldService.isPausedStatus(hold);
  }

  isCancellableStatus(hold: Hold): boolean {
    return HoldService.isCancellableStatus(hold);
  }

  isDeletableStatus(hold: Hold): boolean {
    return HoldService.isDeletableStatus(hold);
  }

  onClickRegisterHere(): void {
    this.dialog.open(CreateAccountDialogComponent);
  }

  hasExternalAvailabilityLink(catalogRecord: CatalogRecord): boolean {
    return catalogRecord?.marcDetails?.urls?.length > 0;
  }

  getExternalAvailabilityLink(catalogRecord: CatalogRecord): string {
    return catalogRecord.marcDetails.urls[0];
  }
}
